import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {useTranslation} from '@aboutyou/i18n';
import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {
  useTrackFeature,
  useTrackFeatureInViewImpression,
} from '@aboutyou/tracking';
import {Button, FluidGrid, Flyout} from '@aboutyou/ui-components';
import React from 'react';
import styled from 'styled-components';
import {useMiniAdpDialogOpenedState} from '../../../../features/MiniADPDialog/useMiniAdpDialogOpenedState';
import {ProductMiniAdp} from '../../../../features/ProductMiniAdp/ProductMiniAdp';
import {ProductTile} from '../../../../features/ProductTile/ProductTile';
import {AddToBasketPayload} from '../../../../features/ProductTile/types';
import {SizeSelectionOpenedStateKey} from '../../../../features/SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';
import {RecommendationsComponentProps} from '../BasketMOVView';

export const BasketMOVBottomsheetRecos: React.FC<
  RecommendationsComponentProps
> = ({
  movUpsellingRecommendationSection,
  onAddToBasketSuccess,
  refetchBasketSection,
}) => {
  const {open, isOpen, cancel} = useOpenedStateManager(
    'MOV_UPSELLING_RECOMMENDATION',
  );

  const trackFeature = useTrackFeature();

  const {ref} = useTrackFeatureInViewImpression<HTMLDivElement>({
    label: 'mov_reco',
    name: 'mov_reco',
  });

  const {
    isOpen: isFlyoutOpen,
    close: closeFlyout,
    open: openFlyout,
    productId,
    selectedSize,
    setSelectedSize,
    couponPromotionId,
  } = useMiniAdpDialogOpenedState();

  const {t} = useTranslation();

  const handleOnClickAddToBasket = (payload: AddToBasketPayload) => {
    openFlyout({
      productId: payload.product.id,
      couponPromotionId: payload.couponPromotionId,
    });
  };

  const onA2BSuccessHandler = async ({
    freeDeliverySection,
    statusSuccessSections,
  }: {
    freeDeliverySection?: FreeDeliverySection;
    statusSuccessSections: AddItemResponseStatus_Success_Section[];
  }) => {
    closeFlyout();
    await refetchBasketSection();
    onAddToBasketSuccess?.({freeDeliverySection, statusSuccessSections});
  };

  const handleOpen = () => {
    trackFeature({
      eventType: 'click',
      label: 'mov_info',
      name: 'mov_info',
    });
    open();
  };

  const movRecoProducts = movUpsellingRecommendationSection.products;

  if (movRecoProducts.length === 0) {
    return null;
  }
  return (
    <Wrapper data-testid="basketMovRecommendation">
      <StyledButton
        variant="secondary"
        onClick={handleOpen}
        data-testid="basketMovRecommendationOpener"
      >
        {movUpsellingRecommendationSection.headline}
      </StyledButton>
      <Flyout
        open={isOpen}
        onClose={cancel}
        bodyContent={
          <RecoWrapper ref={ref}>
            <FluidGrid>
              {movRecoProducts.map((tile, index) => (
                <ProductTile
                  key={tile.productId}
                  tile={tile}
                  streamPosition={index}
                  gridColumns={2}
                  onClickAddCta={handleOnClickAddToBasket}
                  addCtaLabel={t('Add to basket')}
                  trackingData={{
                    sectionIndex: 0,
                    recoType:
                      movUpsellingRecommendationSection.promotionTracking.name,
                    headline: movUpsellingRecommendationSection.headline,
                  }}
                />
              ))}
            </FluidGrid>
          </RecoWrapper>
        }
      />
      <ProductMiniAdp
        close={closeFlyout}
        isOpen={isFlyoutOpen}
        productId={productId}
        selectedSize={selectedSize}
        setSelectedSize={setSelectedSize}
        onSuccess={onA2BSuccessHandler}
        sizeSelectionStateManagerKey={
          SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION
        }
        showToastMessage
        trackingData={{
          sectionIndex: 0,
          recoType: movUpsellingRecommendationSection.promotionTracking.name,
        }}
        couponPromotionId={couponPromotionId}
      />
    </Wrapper>
  );
};

const RecoWrapper = styled.div`
  overflow-y: auto;
`;

const StyledButton = styled(Button)`
  padding: 0.4375rem 0.5rem 0.4375rem 0.75rem;
`;

const Wrapper = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
`;
